import type { z } from 'zod/mini';
import type { envelopeSchema } from './envelope-schema';
import type { RecordType } from './enums';

/** Any record envelope: a union discriminated by `type`. */
export type AnyEnvelope = z.infer<typeof envelopeSchema>;

/** The envelope of one record type, e.g. `Envelope<'timeBlock'>`. */
export type Envelope<T extends RecordType = RecordType> = Extract<AnyEnvelope, { type: T }>;

/** `data` payload type of one record type (non-null). */
export type DataOf<T extends RecordType> = NonNullable<Envelope<T>['data']>;

/** A record that is not deleted: `deleted` is false and `data` is present. */
export type Live<E extends AnyEnvelope> = E & { deleted: false; data: NonNullable<E['data']> };

export type ProjectRecord = Envelope<'project'>;
export type TimeBlockRecord = Envelope<'timeBlock'>;
export type CheckinItemRecord = Envelope<'checkinItem'>;
export type CheckinLogRecord = Envelope<'checkinLog'>;
export type DailyNoteRecord = Envelope<'dailyNote'>;
export type TemplateRecord = Envelope<'template'>;
export type SettingRecord = Envelope<'setting'>;
export type ImportantDateRecord = Envelope<'importantDate'>;

export type LiveProject = Live<ProjectRecord>;
export type LiveTimeBlock = Live<TimeBlockRecord>;
export type LiveCheckinItem = Live<CheckinItemRecord>;
export type LiveCheckinLog = Live<CheckinLogRecord>;
export type LiveDailyNote = Live<DailyNoteRecord>;
export type LiveTemplate = Live<TemplateRecord>;
export type LiveSetting = Live<SettingRecord>;
export type LiveImportantDate = Live<ImportantDateRecord>;

export function isLive<E extends AnyEnvelope>(record: E): record is Live<E> {
  return !record.deleted && record.data !== null;
}

/** Keep only the live records of a list, narrowing their type. */
export function liveOnly<E extends AnyEnvelope>(records: readonly E[]): Live<E>[] {
  return records.filter(isLive) as Live<E>[];
}
